import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Users,
    Briefcase,
    ClipboardList,
    Puzzle,
    ArrowRight,
    ArrowUp,
    ArrowDown,
    Calendar,
    Building2, // Added for Add Company
    Sparkles, // Added for View Matches
    KanbanSquare, // Added for View Kanban
    Loader2, // Added for loading states in modals if needed
    CheckSquare2, // Added for Tasks
    CalendarDays, // Added for Calendar/Events
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { Link, useNavigate } from "react-router-dom"; // Added useNavigate
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Import hooks and components for quick actions
import { useCandidates } from "@/hooks/candidates/useCandidates";
import { useCompanies } from "@/hooks/useCompanies";
import { useJobsData } from "@/hooks/jobs/useJobsData";
import { useCreateTask } from "@/hooks/tasks/useTasks";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { CandidateFormData } from "@/utils/candidate";
import { Candidate } from "@/types/candidate";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { NotificationsWidget } from "@/components/dashboard/NotificationsWidget";
import { DueTasksWidget } from "@/components/dashboard/DueTasksWidget";
import { toast } from "@/hooks/use-toast";
import { useEvents } from "@/hooks/useEvents";
import { CreateTaskForm } from "@/components/tasks/CreateTaskForm";

const Dashboard = () => {
	usePageTitle("Dashboard");
	const { t } = useTranslation();
	const navigate = useNavigate(); // Initialize navigate
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    const [chartView, setChartView] = useState<"day" | "week" | "month">("day");

    // Hooks for data fetching and mutations (needed for quick actions)
    // Note: useCandidates hook might need adjustment if it wasn't designed for mutations here
    const { candidates, setCandidates } = useCandidates(); // Assuming useCandidates provides setCandidates
    const { addCompany, fetchCompanies } = useCompanies(); // Assuming useCompanies provides these
    const { addJob } = useJobsData(); // Assuming useJobsData provides addJob
    const createTaskMutation = useCreateTask();

    // State for modals
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);
    const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
    const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
    const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
    const [prefillFormData, setPrefillFormData] =
        useState<CandidateFormData | null>(null);

    // Fetch dashboard stats (existing code)
    const { data: statsData, isLoading: statsLoading } = useQuery({
        queryKey: ["dashboardStats"],
        queryFn: async () => {
            // ... existing stats fetching logic ...
            try {
                // Fetch candidates count
                const { count: candidatesCount, error: candidatesError } =
                    await supabase
                        .from("candidates")
                        .select("id", { count: "estimated", head: true });

                if (candidatesError) throw candidatesError;

                // Fetch companies count
                const { count: companiesCount, error: companiesError } =
                    await supabase
                        .from("companies")
                        .select("id", { count: "estimated", head: true });

                if (companiesError) throw companiesError;

                // Fetch jobs count
                const { count: jobsCount, error: jobsError } = await supabase
                    .from("jobs")
                    .select("id", { count: "estimated", head: true });

                if (jobsError) throw jobsError;

                // Fetch matches count
                const { count: matchesCount, error: matchesError } =
                    await supabase
                        .from("matches")
                        .select("id", { count: "estimated", head: true });

                if (matchesError) throw matchesError;

                return [
                    {
                        name: "Candidates",
                        count: candidatesCount || 0,
                        icon: Users,
                        color: "bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400",
                        change: 12, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/candidates", // Added link for consistency
                    },
                    {
                        name: "Companies",
                        count: companiesCount || 0,
                        icon: Building2, // Changed icon to Building2 for consistency
                        color: "bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400",
                        change: 5, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/companies", // Added link for consistency
                    },
                    {
                        name: "Jobs",
                        count: jobsCount || 0,
                        icon: Briefcase, // Changed icon to Briefcase for consistency
                        color: "bg-amber-50 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400",
                        change: 3, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/jobs", // Added link for consistency
                    },
                    {
                        name: "Matches",
                        count: matchesCount || 0,
                        icon: Sparkles, // Changed icon to Sparkles for consistency
                        color: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400",
                        change: 8, // Placeholder change data
                        isPositive: true, // Placeholder change data
                        link: "/matches", // Added link for consistency
                    },
                ];
            } catch (error) {
                console.error("Error fetching dashboard stats:", error);
                // Return default structure on error to prevent crashes
                return [
                    { name: "Candidates", count: 0, icon: Users, color: "bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400", change: 0, isPositive: true, link: "/candidates" },
                    { name: "Companies", count: 0, icon: Building2, color: "bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400", change: 0, isPositive: true, link: "/companies" },
                    { name: "Jobs", count: 0, icon: Briefcase, color: "bg-amber-50 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400", change: 0, isPositive: true, link: "/jobs" },
                    { name: "Matches", count: 0, icon: Sparkles, color: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400", change: 0, isPositive: true, link: "/matches" },
                ];
            }
        },
    });

    // Fetch matches data for chart based on selected view

    
    // Helper function to get week number of a date
    const getWeekNumber = (date: Date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    // Fetch upcoming events using the new events table
    const { data: allEvents, isLoading: eventsLoading } = useEvents();
    
    // Filter events for the next 14 days
    const upcomingEvents = allEvents?.filter(event => {
        const eventDate = new Date(event.start_time);
        const now = new Date();
        const twoWeeksFromNow = new Date();
        twoWeeksFromNow.setDate(now.getDate() + 14);
        
        return eventDate >= now && eventDate <= twoWeeksFromNow;
    }).slice(0, 3) || [];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    // Handlers from previous version
    const handleOpenAddCandidateModal = () => {
        setIsSelectionModalOpen(true);
    };

    const handleManualCreate = () => {
        setIsSelectionModalOpen(false);
        setPrefillFormData(null);
        setIsAddFormModalOpen(true);
    };

    const handleCVUpload = (formData: CandidateFormData) => {
        setIsSelectionModalOpen(false);
        setPrefillFormData(formData);
        setIsAddFormModalOpen(true);
    };

    const handleCandidateAdded = (newCandidate: Candidate) => {
        // Optimistically update candidate list or refetch
        if (setCandidates) {
            setCandidates((prev: Candidate[] | undefined) => [
                ...(prev || []),
                newCandidate,
            ]);
        }
        setIsAddFormModalOpen(false);
        setPrefillFormData(null);
        toast({
            title: "Success",
            description: "Candidate added successfully.",
        });
        // Optionally navigate: navigate(`/candidates/${newCandidate.id}`);
    };

    const handleOpenAddCompanyModal = () => {
        setIsAddCompanyModalOpen(true);
    };

    const handleCompanySubmit = async (companyData: any) => {
        try {
            const success = await addCompany(companyData); // Assumes addCompany returns boolean/data
            if (success) {
                await fetchCompanies(); // Refetch companies list
                toast({
                    title: "Success",
                    description: "Company created successfully",
                });
                setIsAddCompanyModalOpen(false); // Close modal on success
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error adding company:", error);
            toast({
                title: "Error",
                description: "Failed to create company",
                variant: "destructive",
            });
            return false;
        }
    };

    const handleOpenAddJobModal = () => {
        setIsAddJobModalOpen(true);
    };

    const handleAddJob = async (jobData: any) => {
        try {
            const jobId = await addJob(jobData); // Assumes addJob returns ID or data
            if (jobId) {
                toast({
                    title: "Success",
                    description: "Job offer created successfully",
                });
                setIsAddJobModalOpen(false); // Close modal on success
                // Optionally refetch jobs or navigate
                return jobId;
            }
            return undefined;
        } catch (error) {
            console.error("Error adding job:", error);
            toast({
                title: "Error",
                description: "Failed to create job offer",
                variant: "destructive",
            });
            return undefined;
        }
    };

    const statisticsData = statsData || [];
    const events = upcomingEvents || [];

    return (
        <>
            <div className="space-y-8">
                <PageHeader
                    title={t("dashboard.title")}
                    description={t("dashboard.welcome")}
                />

            {/* Statistics */}
            <div className="flex flex-wrap gap-4">
                {statsLoading
                    ? Array(4).fill(0).map((_, i) => (
                        <div key={i} className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
                    ))
                    : statisticsData.map((stat) => (
                        <Link
                            key={stat.name}
                            to={stat.link || "#"}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
                        >
                            <stat.icon className={cn("h-3.5 w-3.5", stat.color.split(" ").filter(c => c.startsWith("text-")).join(" "))} />
                            <span className="text-sm font-semibold">{stat.count}</span>
                            <span className="text-sm text-muted-foreground">{t(`dashboard.stats.${stat.name.toLowerCase()}`)}</span>
                        </Link>
                    ))}
            </div>

            {/* Chart, Events, Quick Actions, and Notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Notifications Widget - Now in the main position (2/3 width) */}
                <div className="lg:col-span-2">
                    <NotificationsWidget />
                </div>

                {/* Quick Actions Card (1/3 width) */}
                <Card data-tour="dashboard-quick-actions">
                    <CardHeader>
                        <CardTitle>{t("dashboard.quickActions")}</CardTitle>
                        <CardDescription>
                            {t("dashboard.quickActionsDesc")}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-y-3">
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddCandidateModal}
                        >
                            <Users className="mr-2 h-4 w-4" />
                            {t("dashboard.addCandidate")}
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddCompanyModal}
                        >
                            <Building2 className="mr-2 h-4 w-4" />
                            {t("dashboard.addCompany")}
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={handleOpenAddJobModal}
                        >
                            <Briefcase className="mr-2 h-4 w-4" />
                            {t("dashboard.addJobOffer")}
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                        >
                            <Link to="/matches">
                                <Sparkles className="mr-2 h-4 w-4" />
                                {t("dashboard.viewMatches")}
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                        >
                            <Link to="/kanban">
                                <KanbanSquare className="mr-2 h-4 w-4" />
                                {t("dashboard.viewKanban")}
                            </Link>
                        </Button>
                        <Button
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                            onClick={() => setIsAddTaskModalOpen(true)}
                        >
                            <CheckSquare2 className="mr-2 h-4 w-4" />
                            {t("dashboard.addTask", "Add Task")}
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="w-full justify-start whitespace-normal break-words min-w-0"
                        >
                            <Link to="/calendar">
                                <CalendarDays className="mr-2 h-4 w-4" />
                                {t("dashboard.viewCalendar", "View Calendar")}
                            </Link>
                        </Button>
                    </CardContent>
                    {/* No Footer needed for Quick Actions? Or add a general link? */}
                </Card>
            </div>

            {/* Due Tasks & Upcoming Events side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DueTasksWidget />

            {/* Upcoming Events Card */}
            <Card>
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <div>
                                <CardTitle>{t("dashboard.upcomingEvents")}</CardTitle>
                                <CardDescription>
                                    {t("dashboard.next14Days")}
                                </CardDescription>
                            </div>
                            <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        {eventsLoading ? (
                            <div className="space-y-4">
                                {Array(3)
                                    .fill(0)
                                    .map((_, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3 animate-pulse"
                                        >
                                            <div className="bg-gray-200 dark:bg-gray-700 rounded-sm p-2 h-8 w-8"></div>
                                            <div className="space-y-2 flex-1">
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        ) : upcomingEvents.length > 0 ? (
                            <div className="space-y-4">
                                {upcomingEvents.map((event) => {
                                    const eventDate = new Date(event.start_time);
                                    const formattedDate =
                                        new Intl.DateTimeFormat("en-US", {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        }).format(eventDate);

                                    return (
                                        <div
                                            key={event.id}
                                            className="flex items-start gap-3"
                                        >
                                            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-sm p-2 mt-1">
                                                <Calendar className="h-4 w-4 text-blue-500" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-sm">
                                                    {event.title}
                                                </h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {formattedDate}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground py-8">
                                {t("dashboard.noUpcomingEvents")}
                            </div>
                        )}
                    </CardContent>
                    <CardFooter>
                        <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="ml-auto"
                        >
                            <Link to="/calendar">
                                {t("dashboard.viewCalendar")}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </CardFooter>
                </Card>
            </div>

            </div>

            {/* Modals */}
            <AddCandidateSelectionModal
                isOpen={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                onManualCreate={handleManualCreate}
                onCVUpload={handleCVUpload}
            />

            <AddCandidateForm
                isOpen={isAddFormModalOpen}
                onClose={() => setIsAddFormModalOpen(false)}
                onCandidateAdded={handleCandidateAdded}
                initialFormData={prefillFormData}
            />

            <AddCompanyForm
                isOpen={isAddCompanyModalOpen}
                onClose={() => setIsAddCompanyModalOpen(false)}
                onSubmit={handleCompanySubmit}
            />

            <JobOffersAddModal
                isOpen={isAddJobModalOpen}
                onClose={() => setIsAddJobModalOpen(false)}
                onAddJob={handleAddJob}
            />

            <CreateTaskForm
                isOpen={isAddTaskModalOpen}
                onClose={() => setIsAddTaskModalOpen(false)}
                onSubmit={() => setIsAddTaskModalOpen(false)}
            />
        </>
    );
};

export default Dashboard;